import { useEffect, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Radio, Row, Select, Space, Switch, Table, Tooltip } from 'antd';
import { useHistory } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';

import { Constants, DateRangeOption, FilterType, TransactionType } from 'common/constants';
// import moment from 'moment';
import { GetTransactionByRefNo, TransactionActions, TransactionSelector, SearchBy } from '@features/Transaction/index';
import dayjs, { Dayjs } from 'dayjs'; // Import dayjs
import { FilterSearchParameters, SearchParameters, TransactionSearchResult } from 'common/models/BookingTypes';
import { OperationStatus } from 'store/rootTypes';
import { TableLocaleCustom } from '@components/Custom/CustomTable';
import { GetHeaders } from '@features/Common';
import { KFSEndpoints } from 'common/constants/KFSEndpoints';
import { apibaseUrl } from 'common/utils/jsonFetch';
import { SortOrder } from 'antd/es/table/interface';
import { PATHS } from '@routing/routes';
import { EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { CommonDropdownCodeList, StateFilterCodeList } from 'common/code/DropdownCode';
import { CheckUserAccessState } from 'common/utils/utility';
import { AccountSelector } from '@features/Account';
import { EmployeeGetAllAgent, EmployeeSelector } from '@features/Employee';
// 2026-08-22 US-009: Creation date sorting. CreatedDate arrives as a plain string from stored procedure
// GetTransactionByEmail, which is not in source control — nothing in this repo pins its format. Live data is
// YYYY-MM-DD, but this same API formats dates as dd-MMM-yyyy elsewhere (TransactionService.Save), so both are
// parsed rather than trusting one. Deliberately NOT new Date(): that returns Invalid Date for dd-MMM-yyyy, and a
// NaN-returning comparator leaves rows in arbitrary order while antd still renders a sort arrow — a wrong order
// presented to the operator as authoritative. Returns null, never NaN, for anything it cannot parse.
const MONTH_ABBREVIATIONS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

const parseCreationDate = (value?: string | null): number | null => {
  const text = value?.trim();
  if (!text) {
    return null;
  }

  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (isoMatch) {
    return Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  const dayMonthYearMatch = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(text);
  if (dayMonthYearMatch) {
    const monthIndex = MONTH_ABBREVIATIONS.indexOf(dayMonthYearMatch[2].toLowerCase());
    if (monthIndex >= 0) {
      return Date.UTC(Number(dayMonthYearMatch[3]), monthIndex, Number(dayMonthYearMatch[1]));
    }
  }

  return null;
};

// 2026-08-22 US-009: rows with a missing or unrecognised date group together with the oldest, so they stay
// contiguous at one end in both directions and are never dropped from the grid by sorting.
const compareCreationDate = (a: TransactionSearchResult, b: TransactionSearchResult) => {
  const left = parseCreationDate(a.CreatedDate);
  const right = parseCreationDate(b.CreatedDate);
  if (left === null && right === null) {
    return 0;
  }
  if (left === null) {
    return -1;
  }
  if (right === null) {
    return 1;
  }
  return left - right;
};

export const Transaction = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const defaultDate = dayjs();
  const transactionState = useSelector(TransactionSelector);
  const accountState = useSelector(AccountSelector);
  const defaultDateFrom = dayjs().subtract(1, 'day');
  const employeeAgentState = useSelector(EmployeeSelector);
  const [form] = Form.useForm();
  let data: TransactionSearchResult[] = transactionState.TransactionSearchResult || [];
  const statusOptions = Array.from(new Set(data.map((item) => item.Status))).map((status) => ({
    text: status,
    value: status,
  }));
  const paymentStatusOptions = Array.from(new Set(data.map((item) => item.PaymentStatus)))
    .filter((ps): ps is string => !!ps)
    .map((PaymentStatus) => ({ text: PaymentStatus, value: PaymentStatus }));
  // 2026-08-14 US-006: creation-date filter, built the same way as the Status and Payment
  // status options above — distinct values taken from the loaded result set, so the list
  // only ever offers dates that actually appear in the current search. Sorted newest first
  // rather than alphabetically: chronological order is what people expect of dates. The API
  // returns CreatedDate date-only (YYYY-MM-DD), which keeps the list one entry per day and
  // makes a plain string sort chronological — if a time component is ever added, the values
  // must be truncated to the date here or the list becomes one entry per transaction.
  const creationDateOptions = Array.from(new Set(data.map((item) => item.CreatedDate)))
    .filter((d): d is string => !!d)
    .sort((a, b) => b.localeCompare(a))
    .map((CreatedDate) => ({ text: CreatedDate, value: CreatedDate }));

  const handlePageChange = (page) => {
    dispatch(
      TransactionActions.SetTransactionsSearch({
        ...transactionState.TransactionsSearch,
        PageNo: page,
      })
    );
  };

  const [filteredData, setFilteredData] = useState(data);
  const handleSearchChange = (e) => {
    const text = e.target.value;
    FunctionSearch(text);
  };

  const FunctionSearch = (text: string) => {
    dispatch(
      TransactionActions.SetTransactionsSearch({
        ...transactionState.TransactionsSearch, // Preserve existing state
        SearchText: text, // Update SearchText
      })
    );
    // If search box is empty, reset to the original data
    if (!text) {
      setFilteredData(data);
      return;
    }

    // Filter data based on the search text
    const filtered = data.filter((item) =>
      Object.values(item).some((value) => String(value).toLowerCase().includes(text.toLowerCase()))
    );

    setFilteredData(filtered);
  };

  const onChangeFlag = async (checked: boolean) => {
    dispatch(TransactionActions.SetIsFlag(checked));
    if (checked) {
      await dispatch(SearchBy({ IsFlag: checked }));
    } else {
      setFilteredData([]);
    }
  };

  const columns = [
    {
      title: 'Reference no',
      dataIndex: 'RefNo',
      key: 'RefNo',
      render: (text, record) => (
        <span>
          <a onClick={() => handleReferenceClick(record.Id, record.Type)} className={record.IsFlag ? 'isFlagTrue' : ''}>
            {text}
          </a>
          {record.Remarks && record.Remarks.trim() !== '' ? (
            <Tooltip title={<span dangerouslySetInnerHTML={{ __html: record.Remarks }} />} placement="top">
              <a className="pl-10">
                <EyeOutlined />
              </a>
            </Tooltip>
          ) : null}
        </span>
      ),
    },
    {
      title: 'Sender',
      dataIndex: 'Sender',
      key: 'Sender',
    },
    {
      title: 'Suburb',
      dataIndex: 'Suburb',
      key: 'Suburb',
    },
    {
      title: 'Qty',
      dataIndex: 'Qty',
      key: 'Qty',
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      filters: statusOptions,
      onFilter: (value: string | number | boolean, record: TransactionSearchResult) => {
        if (typeof value === 'string') {
          return record.Status.includes(value);
        }
        return false;
      },
      sorter: (a: TransactionSearchResult, b: TransactionSearchResult) =>
        (a.Status as string).localeCompare(b.Status as string),
    },
    {
      title: 'Schedule',
      dataIndex: 'PickupDate',
      key: 'PickupDate',
      sorter: (a: TransactionSearchResult, b: TransactionSearchResult) =>
        new Date(a.PickupDate).getTime() - new Date(b.PickupDate).getTime(),
      sortDirections: ['ascend', 'descend'] as SortOrder[],
    },
    {
      title: 'Amount due',
      dataIndex: 'AmountDue',
      key: 'AmountDue',
    },
    {
      title: 'Payment status',
      dataIndex: 'PaymentStatus',
      key: 'PaymentStatus',
      filters: paymentStatusOptions,
      onFilter: (value: string | number | boolean, record: TransactionSearchResult) => {
        if (typeof value === 'string') {
          return record.PaymentStatus?.includes(value) ?? false;
        }
        return false;
      },
      sorter: (a: TransactionSearchResult, b: TransactionSearchResult) =>
        (a.PaymentStatus as string).localeCompare(b.PaymentStatus as string),
    },
    {
      title: 'Creation date',
      dataIndex: 'CreatedDate',
      key: 'CreatedDate',
      filters: creationDateOptions,
      // 2026-08-14 US-006: exact match, unlike the substring test the Status and Payment
      // status columns use. A substring would let the value "2026-06-0" also match
      // 2026-06-01 through 2026-06-09 — harmless for words, wrong for dates.
      onFilter: (value: string | number | boolean, record: TransactionSearchResult) =>
        typeof value === 'string' ? record.CreatedDate === value : false,
      // A search returning 100 rows can hold 60+ distinct dates, where Status holds under
      // ten. This lets the operator type to narrow the list instead of scrolling it.
      filterSearch: true,
      // 2026-08-22 US-009: sorts the whole loaded result set, not just the visible page — SearchBy returns every
      // matching row and the Table paginates client-side, so the comparator sees all of them. Coexists with the
      // US-006 filter above: antd applies the filter first, then sorts what survives.
      sorter: compareCreationDate,
      sortDirections: ['ascend', 'descend'] as SortOrder[],
    },
  ];
  const handleReferenceClick = async (id, type) => {
    dispatch(TransactionActions.SetSelectedTransId(id));
    dispatch(TransactionActions.SetTransactionType(type));
  };

  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null); // Explicitly define the type

  const disabledDateTo = (current: Dayjs | null) => {
    // Disable dates earlier than the selected date in "Date from"
    return dateFrom && current && current.isBefore(dateFrom.startOf('day'));
  };

  const handleDateFromChange = (value: Dayjs | null) => {
    setDateFrom(value);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (transactionState.TransactionType == TransactionType.OrderBox && transactionState.SelectedTransId) {
        history.push(PATHS.SummaryOrderBox.path);
      } else if (transactionState.SelectedTransId) {
        history.push('/transaction/summary');
      }
    };
    dispatch(TransactionActions.SetFromUpdateLinkFalse());
    fetchData();
  }, [transactionState?.TransactionType, transactionState?.SelectedTransId]);

  useEffect(() => {
    data = transactionState.TransactionSearchResult || [];
    setFilteredData(data);
    // setSearchText(transactionState.TransactionsSearch.SearchText);
    if (transactionState.TransactionsSearch.SearchText) {
      FunctionSearch(transactionState.TransactionsSearch.SearchText);
    }
    // alert(transactionState.TransactionsSearch.SearchText);
    // alert(transactionState?.FilterSearchParameters?.Status);
  }, [transactionState.TransactionSearchResult]);

  useEffect(() => {
    const fetchData1 = async () => {
      if (!employeeAgentState.Employee) {
        await dispatch(EmployeeGetAllAgent());
      }
    };
    fetchData1();
  }, []);

  const [searchParams, setSearchParams] = useState<SearchParameters>({
    Email: null,
    State: null,
    PickupFrom: null,
    PickupTo: null,
    CreatedFrom: null,
    CreatedTo: null,
    Status: null,
    AgentId: null,
  });

  const [tableKey, setTableKey] = useState(0);

  const onFinish = async (values: any) => {
    dispatch(TransactionActions.SetIsFlag(false));
    // alert(JSON.stringify(values));
    dispatch(
      TransactionActions.SetTransactionsSearch({
        SearchText: '',
        PageNo: 1,
      })
    );
    setTableKey((tableKey) => tableKey + 1);
    if (transactionState.FilterType === FilterType.RefNo) {
      const filterSearchParameters: FilterSearchParameters = {
        RefNo: values.RefNo,
      };
      dispatch(TransactionActions.SetFilterSearchParameters(filterSearchParameters));
      await dispatch(GetTransactionByRefNo(values.RefNo));
    } else if (transactionState.FilterType === FilterType.Email) {
      const searchParams: SearchParameters = {
        Email: values.Email,
      };
      const filterSearchParameters: FilterSearchParameters = {
        Email: values.Email,
      };
      dispatch(TransactionActions.SetFilterSearchParameters(filterSearchParameters));
      setSearchParams(searchParams);
      await dispatch(SearchBy(searchParams));
    } else if (transactionState.FilterType === FilterType.Date && values.DateType === DateRangeOption.Pickup) {
      const filterSearchParameters: FilterSearchParameters = {
        DateType: values.DateType,
        DateFrom: values.DateFrom,
        DateTo: values.DateTo,
        Status: values.Status?.join(','),
        State: values.State,
        AgentId: values.AgentId?.join(','),
        PaymentStatus: values.PaymentStatus || null,
      };
      dispatch(TransactionActions.SetFilterSearchParameters(filterSearchParameters));

      const searchParams: SearchParameters = {
        PickupFrom: values.DateFrom,
        PickupTo: values.DateTo,
        Status: values.Status?.join(','),
        State: values.State,
        AgentId: values.AgentId?.join(','),
        PaymentStatus: values.PaymentStatus || null,
      };
      setSearchParams(searchParams);
      await dispatch(SearchBy(searchParams));
    } else if (transactionState.FilterType === FilterType.Date && values.DateType === DateRangeOption.Booking) {
      const filterSearchParameters: FilterSearchParameters = {
        DateType: values.DateType,
        DateFrom: values.DateFrom,
        DateTo: values.DateTo,
        Status: values.Status?.join(','),
        State: values.State,
        AgentId: values.AgentId?.join(','),
        PaymentStatus: values.PaymentStatus || null,
      };
      dispatch(TransactionActions.SetFilterSearchParameters(filterSearchParameters));

      const searchParams: SearchParameters = {
        CreatedFrom: values.DateFrom,
        CreatedTo: values.DateTo,
        Status: values.Status?.join(','),
        State: values.State,
        AgentId: values.AgentId?.join(','),
        PaymentStatus: values.PaymentStatus || null,
      };
      setSearchParams(searchParams);
      await dispatch(SearchBy(searchParams));
    }
  };

  const convertToDayjs = (date) => {
    return date ? dayjs(date) : null;
  };

  function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Adding 1 because months are zero-based
    const day = date.getDate().toString().padStart(2, '0');

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[date.getMonth()];

    return `${year}-${monthName}-${day}`;
  }

  const generateExcel = async () => {
    // console.log(JSON.stringify(transactionState.FilterSearchParameters));
    // console.log(JSON.stringify(searchParams));

    const params = transactionState.FilterSearchParameters || {};
    let mappedSearchParams = {};

    if (params.DateType === 'P') {
      mappedSearchParams = {
        PickupFrom: params.DateFrom,
        PickupTo: params.DateTo,
        Status: params.Status,
        State: params.State,
        AgentId: params.AgentId,
        PaymentStatus: params.PaymentStatus,
      };
    } else if (params.DateType === 'B') {
      mappedSearchParams = {
        CreatedFrom: params.DateFrom,
        CreatedTo: params.DateTo,
        Status: params.Status,
        State: params.State,
        AgentId: params.AgentId,
        PaymentStatus: params.PaymentStatus,
      };
    } else {
      mappedSearchParams = {
        Email: params.Email,
      };
      // console.warn('⚠️ Unknown DateType:', params.DateType);
    }

    // console.log(mappedSearchParams);
    try {
      const apiHeaders = await GetHeaders(apibaseUrl + KFSEndpoints.Transaction_GenerateExcel);
      const response = await fetch(apibaseUrl + KFSEndpoints.Transaction_GenerateExcel, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(mappedSearchParams), //JSON.stringify(transactionState.FilterSearchParameters), // Convert your data to JSON if required
      });
      const blob = await response.blob();
      // Create a download link and trigger a download
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      const currentDate = new Date();
      const formattedDate = 'Transaction-' + formatDate(currentDate) + '.xlsx';
      link.setAttribute('download', formattedDate);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating Excel file', error);
    }
  };

  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const handleSelectChange = (values) => {
    // alert(values.length);
    //alert(values[values.length - 1]);
    if ((values.includes('All') && values.length == 1) || (values.length > 1 && values[values.length - 1]) == 'All') {
      // If "All" is selected, keep only "All" and remove other options
      const filteredValues = ['All'];
      setSelectedValues(filteredValues); // Update the state
      form.setFieldsValue({ Status: filteredValues }); // Sync with Form
    } else {
      // If any other option is selected, remove "All" if it exists
      const filteredValues = values.filter((value) => value !== 'All');
      setSelectedValues(filteredValues); // Update the state
      form.setFieldsValue({ Status: filteredValues }); // Sync with Form
    }
  };

  const [selectedValuesAgent, setSelectedValuesAgent] = useState<string[]>([]);
  const handleSelectChangeAgent = (values) => {
    // alert(values.length);
    //alert(values[values.length - 1]);
    if ((values.includes('All') && values.length == 1) || (values.length > 1 && values[values.length - 1]) == 'All') {
      // If "All" is selected, keep only "All" and remove other options
      const filteredValues = ['All'];
      setSelectedValuesAgent(filteredValues); // Update the state
      form.setFieldsValue({ AgentId: filteredValues }); // Sync with Form
    } else {
      // If any other option is selected, remove "All" if it exists
      const filteredValues = values.filter((value) => value !== 'All');
      setSelectedValuesAgent(filteredValues); // Update the state
      form.setFieldsValue({ AgentId: filteredValues }); // Sync with Form
    }
  };
  return (
    <div className="App">
      <Row justify="center" align="middle">
        {' '}
        {transactionState.FilterType === FilterType.RefNo ? (
          <h1>Search by Reference nbr / Barcode / Invoice</h1>
        ) : transactionState.FilterType === FilterType.Email ? (
          <h1>Search by Email / Mobile</h1>
        ) : (
          <h1>Search by date, status, state</h1>
        )}
      </Row>
      <Form name="senderForm" form={form} onFinish={onFinish} layout="vertical">
        <Row justify="center" align="middle">
          <Col xs={24} sm={20} md={16} lg={16} xl={8}>
            {transactionState.FilterType === FilterType.RefNo && (
              <Form.Item
                label=""
                name="RefNo"
                initialValue={transactionState.FilterSearchParameters?.RefNo}
                rules={[{ required: true, message: Constants.REQUIRED_FIELD }]}
              >
                <Input size="large" maxLength={150} />
              </Form.Item>
            )}
            {transactionState.FilterType === FilterType.Email && (
              <Form.Item
                label=""
                name="Email"
                rules={[
                  { required: true, message: Constants.REQUIRED_FIELD },
                  {
                    min: 6,
                    message: 'Input must be at least 6 characters long.',
                  },
                  {
                    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$|^\+?\d{6,}$/,
                    message: 'Please enter a valid email address or mobile number.',
                  },
                ]}
                // rules={[
                //   { required: true, message: Constants.REQUIRED_FIELD },
                //   {
                //     pattern: Constants.EMAIL_REGEX_INPUT,
                //     message: 'Please enter a valid email address.',
                //   },
                // ]}
                initialValue={transactionState.FilterSearchParameters?.Email}
              >
                <Input size="large" maxLength={150} />
              </Form.Item>
            )}
            {transactionState.FilterType === FilterType.Date && (
              <div>
                <Form.Item
                  label="Date type"
                  name="DateType"
                  initialValue={transactionState.FilterSearchParameters?.DateType || DateRangeOption.Booking}
                  rules={[{ required: true, message: Constants.REQUIRED_FIELD }]}
                >
                  <Radio.Group className="pl-10">
                    <Space direction="vertical">
                      <Radio value={DateRangeOption.Pickup}>Schedule</Radio>
                      <Radio value={DateRangeOption.Booking}>Booking date</Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                <Row gutter={[10, 0]}>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      initialValue={
                        convertToDayjs(transactionState.FilterSearchParameters?.DateFrom) || defaultDateFrom
                      }
                      label="Date from"
                      name="DateFrom"
                      rules={[{ required: true, message: Constants.REQUIRED_FIELD }]}
                    >
                      <DatePicker
                        size="large"
                        className="full-width"
                        placeholder="Date from"
                        onChange={handleDateFromChange}
                        // Set to present date
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      initialValue={convertToDayjs(transactionState.FilterSearchParameters?.DateTo) || defaultDate}
                      label="Date to"
                      name="DateTo"
                      rules={[{ required: true, message: Constants.REQUIRED_FIELD }]}
                    >
                      <DatePicker size="large" className="full-width" placeholder="Date to" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[10, 0]}>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label="Status"
                      name="Status"
                      initialValue={
                        Array.isArray(transactionState?.FilterSearchParameters?.Status)
                          ? transactionState.FilterSearchParameters?.Status
                          : transactionState?.FilterSearchParameters?.Status
                          ? transactionState.FilterSearchParameters?.Status.split(',')
                          : ['NW']
                      }
                    >
                      <Select
                        size="large"
                        showSearch
                        placeholder="Select status"
                        optionFilterProp="children"
                        mode="multiple"
                        value={selectedValues}
                        onChange={handleSelectChange}
                      >
                        {CommonDropdownCodeList.map((eq) => (
                          <Select.Option key={eq.code} value={eq.code}>
                            {eq.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  {CheckUserAccessState(accountState.Username, accountState.RoleName) && (
                    <>
                      <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                          label="State"
                          name="State"
                          initialValue={transactionState?.FilterSearchParameters?.State ?? 'VIC'}
                        >
                          <Select size="large" showSearch placeholder="Select state" optionFilterProp="children">
                            {StateFilterCodeList.map((eq) => (
                              <Select.Option key={eq.code} value={eq.code}>
                                {eq.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                          label="Agent"
                          name="AgentId"
                          initialValue={
                            Array.isArray(transactionState?.FilterSearchParameters?.AgentId)
                              ? transactionState.FilterSearchParameters?.AgentId
                              : transactionState?.FilterSearchParameters?.AgentId
                              ? transactionState.FilterSearchParameters?.AgentId.split(',')
                              : ['All']
                          }
                        >
                          <Select
                            size="large"
                            showSearch
                            placeholder="Select agent"
                            optionFilterProp="children"
                            mode="multiple"
                            value={selectedValuesAgent}
                            onChange={handleSelectChangeAgent}
                          >
                            <Select.Option key="All" value="All">
                              All
                            </Select.Option>

                            {employeeAgentState.Employee?.map((eq) => (
                              <Select.Option key={eq.UserId} value={eq.UserId}>
                                {eq.FirstName} {eq.LastName}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  {!CheckUserAccessState(accountState.Username, accountState.RoleName) && (
                    <>
                      <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item label="State" name="State" initialValue={accountState.WorkLocationStateCode}>
                          <Select
                            size="large"
                            showSearch
                            placeholder="Select state"
                            optionFilterProp="children"
                            disabled
                          >
                            {StateFilterCodeList.map((eq) => (
                              <Select.Option key={eq.code} value={eq.code}>
                                {eq.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                        <Form.Item
                          label="Agent"
                          name="AgentId"
                          initialValue={
                            Array.isArray(transactionState?.FilterSearchParameters?.AgentId)
                              ? transactionState.FilterSearchParameters?.AgentId
                              : transactionState?.FilterSearchParameters?.AgentId
                              ? transactionState.FilterSearchParameters?.AgentId.split(',')
                              : ['All']
                          }
                        >
                          <Select
                            size="large"
                            showSearch
                            placeholder="Select agent"
                            optionFilterProp="children"
                            mode="multiple"
                            value={selectedValuesAgent}
                            onChange={handleSelectChangeAgent}
                          >
                            <Select.Option key="All" value="All">
                              All
                            </Select.Option>

                            {employeeAgentState.Employee?.filter(
                              (x) => x.State == accountState.WorkLocationStateCode
                            ).map((eq) => (
                              <Select.Option key={eq.UserId} value={eq.UserId}>
                                {eq.FirstName} {eq.LastName}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </>
                  )}
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item
                      label="Payment status"
                      name="PaymentStatus"
                      initialValue={transactionState?.FilterSearchParameters?.PaymentStatus ?? ''}
                    >
                      <Select size="large" placeholder="Select payment status" optionFilterProp="children">
                        <Select.Option value="">All</Select.Option>
                        <Select.Option value="P">Paid</Select.Option>
                        <Select.Option value="UP">Unpaid</Select.Option>
                        <Select.Option value="PP">Partially Paid</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={12} lg={12} xl={12}>
                    <Form.Item label="" name="Flag" className="flagClass">
                      Display flagged records{' '}
                      <Tooltip title="This will override other search criteria." className="pr-10">
                        <InfoCircleOutlined />
                      </Tooltip>
                      <Switch checked={transactionState?.IsFlag} onChange={onChangeFlag} className="custom-switch" />
                    </Form.Item>
                  </Col>
                </Row>
              </div>
            )}
            {transactionState.FilterType === FilterType.RefNo && (
              <div>
                <Button className="prevSenderBtn" size="large" type="default" onClick={() => history.push('/home')}>
                  {Constants.BTN_CLOSE}
                </Button>
                <Button
                  className="nextSenderBtn"
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={transactionState.submitStatus == OperationStatus.pending}
                >
                  View details
                </Button>
              </div>
            )}

            {transactionState.FilterType === FilterType.Email && (
              <div>
                <Button className="prevSenderBtn" size="large" type="default" onClick={() => history.push('/home')}>
                  {Constants.BTN_CLOSE}
                </Button>
                <Button
                  className="nextSenderBtn"
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={transactionState.submitStatus == OperationStatus.pending}
                >
                  Search
                </Button>
              </div>
            )}

            {transactionState.FilterType === FilterType.Date && (
              <div>
                <Button className="prevSenderBtn" size="large" type="default" onClick={() => history.push('/home')}>
                  {Constants.BTN_CLOSE}
                </Button>
                <Button
                  className="nextSenderBtn"
                  size="large"
                  type="primary"
                  htmlType="submit"
                  loading={transactionState.submitStatus == OperationStatus.pending}
                >
                  Search
                </Button>
              </div>
            )}
          </Col>
        </Row>
      </Form>
      {transactionState.FilterType != FilterType.RefNo && (
        <>
          <br />
          {data && data.length > 0 && (
            <>
              <Row gutter={16}>
                <Col>
                  <Button
                    className="prevSenderBtn downloadExceltTransaction"
                    size="middle"
                    type="primary"
                    onClick={generateExcel}
                  >
                    Download
                  </Button>
                </Col>
                <Form>
                  <Col>
                    <Form.Item>
                      <Input
                        size="middle"
                        maxLength={150}
                        onChange={handleSearchChange}
                        value={transactionState.TransactionsSearch.SearchText}
                      />
                    </Form.Item>
                  </Col>
                </Form>
              </Row>
            </>
          )}

          <Table
            key={tableKey}
            // dataSource={data}
            dataSource={filteredData}
            columns={columns}
            locale={TableLocaleCustom}
            pagination={{
              pageSize: 100,
              onChange: handlePageChange,
              current: transactionState.TransactionsSearch.PageNo,
            }}
          />
        </>
      )}
    </div>
  );
};
